import React from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {getSettingsEconomicPath} from "../../../../routes/settingsPaths";

export type DashboardModuleKey = "invoice" | "payment" | "payout" | "subscription" | "refund";

export type DashboardIssueCounts = {
    error: number;
    notice: number;
    total: number;
};

type DashboardModuleCardProps = {
    moduleKey: DashboardModuleKey;
    title: string;
    logRoute: string;
    syncEnabled: boolean;
    setupComplete: boolean;
    lastRun?: string | null;
    issueCounts: DashboardIssueCounts;
    showRunSync?: boolean;
    onRunSync?: () => void;
    runSyncLoading?: boolean;
    runSyncDisabled?: boolean;
    /** e.g. Invoice under customer_balance_ledger */
    accountingBlocked?: boolean;
    accountingBlockedHelp?: string;
};

const emptyIssues: DashboardIssueCounts = {error: 0, notice: 0, total: 0};

const DashboardModuleCard: React.FC<DashboardModuleCardProps> = ({
    moduleKey,
    title,
    logRoute,
    syncEnabled,
    setupComplete,
    lastRun,
    issueCounts = emptyIssues,
    showRunSync = false,
    onRunSync,
    runSyncLoading = false,
    runSyncDisabled = false,
    accountingBlocked = false,
    accountingBlockedHelp,
}) => {
    const {t, i18n} = useTranslation();
    const issues = issueCounts.total;
    const errors = issueCounts.error;
    const notices = issueCounts.notice;

    const statusBadge = (() => {
        if (accountingBlocked) {
            return {
                label: t("common.dashboard.ledgerSkipped"),
                className: "bg-secondary",
            };
        }
        if (!syncEnabled) {
            return {label: t("common.dashboard.syncOff"), className: "bg-secondary"};
        }
        if (!setupComplete) {
            return {label: t("economic.setupIncomplete"), className: "bg-warning text-dark"};
        }
        if (errors > 0) {
            return {label: t("common.dashboard.hasErrors", {count: errors}), className: "bg-danger"};
        }
        if (notices > 0) {
            return {label: t("common.dashboard.hasNotices", {count: notices}), className: "bg-warning text-dark"};
        }
        return {label: t("common.dashboard.moduleReady"), className: "bg-success"};
    })();

    const formattedLastRun = lastRun
        ? moment(lastRun).locale(i18n.language).format("DD/MM/YYYY HH:mm")
        : t("common.dashboard.neverRun");

    return (
        <div className="col">
            <div className={`business-detail bg-orange-bland-zp bdr-20 pd-25 h-100 dashboard-module-card d-flex flex-column${accountingBlocked ? " opacity-90" : ""}`}>
                <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                    <h2 className="fw-bold fs-18 mb-0">{title}</h2>
                    <span className={`badge flex-shrink-0 ${statusBadge.className}`}>
                        {statusBadge.label}
                    </span>
                </div>

                {accountingBlocked ? (
                    <p className="text-muted small mb-2">
                        {accountingBlockedHelp || t("economic.accountingModeInvoiceSkippedHelp")}
                    </p>
                ) : (
                    <React.Fragment>
                        <p className="text-muted small mb-1">
                            {t("common.dashboard.lastRun")}: {formattedLastRun}
                        </p>
                        {issues > 0 ? (
                            <p className="text-danger small mb-2">
                                {t("common.dashboard.issueCount", {
                                    error: errors,
                                    notice: notices,
                                })}
                            </p>
                        ) : (
                            <p className="text-muted small mb-2">{t("common.dashboard.noRecentIssues")}</p>
                        )}
                    </React.Fragment>
                )}

                <div className="d-flex flex-wrap align-items-center gap-2 mt-auto pt-2">
                    {showRunSync && !accountingBlocked ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={runSyncDisabled || runSyncLoading}
                            onClick={onRunSync}
                            title={t("common.dashboard.runSyncNow")}
                        >
                            {runSyncLoading ? (
                                <i className="las la-spinner la-spin me-1"/>
                            ) : (
                                <i className="lab la-telegram me-1"/>
                            )}
                            {t("common.dashboard.runSyncNow")}
                        </button>
                    ) : null}
                    <Link
                        to={logRoute}
                        className="btn btn-sm bg-orange-zp text-white"
                        title={t("common.dashboard.viewLogs")}
                        aria-label={t("common.dashboard.viewLogs")}
                    >
                        <i className="las la-list"/>
                    </Link>
                    <Link
                        to={`${getSettingsEconomicPath()}?setup=${moduleKey === "invoice" && accountingBlocked ? "common" : moduleKey}`}
                        className="btn btn-sm btn-outline-secondary"
                        title={accountingBlocked
                            ? t("common.dashboard.openAccountingMode")
                            : t("common.dashboard.moduleSettings")}
                        aria-label={accountingBlocked
                            ? t("common.dashboard.openAccountingMode")
                            : t("common.dashboard.moduleSettings")}
                    >
                        <i className="las la-cog"/>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default React.memo(DashboardModuleCard);
